import React from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import type {
  CategoryWithChildren,
  CategoryWithParentCategory,
  StoryKeyIndicatorsWithValues,
  StoryWithKeyIndicatorsCategoriesStoryImageAndResources,
} from '@aqua/db/types'

import { StoryTypes } from '~/enums/enums'
import { useCategoryFinder } from '~/hooks/story/useCategoryFinder'
import { useChangeCalculation } from '~/hooks/story/useChangeCalculation'

const StoryKeyIndicatorsTableRow = ({
  keyIndicator,
  categoryData,
  story,
}: {
  keyIndicator: StoryKeyIndicatorsWithValues
  categoryData: CategoryWithChildren[]
  story: {
    storiesData: StoryWithKeyIndicatorsCategoriesStoryImageAndResources
    innermostParents: {
      storyId: number
      type: CategoryWithParentCategory
    }
  }
}) => {
  const {
    value: change,
    before,
    after,
  } = useChangeCalculation(keyIndicator, true)

  const indicatorCategories = useCategoryFinder(categoryData, 11)

  const indicatorCategory = indicatorCategories
    .find(
      (option) =>
        option?.children?.some(
          (child) => Number(child.id) === keyIndicator.type,
        ),
    )
    ?.children?.find((child) => Number(child.id) === keyIndicator.type)

  const indicatorCategoryBackground = indicatorCategories?.find(
    (option) => option?.children?.includes(indicatorCategory!),
  )

  return (
    <TableRow>
      <TableCell>{keyIndicator.variableName}</TableCell>
      <TableCell>
        <Badge
          style={{
            backgroundColor: indicatorCategoryBackground?.background ?? '#eee',
          }}
          className={`rounded-full p-2 text-xs font-[500] leading-none text-[#26366b]`}
        >
          {indicatorCategory?.name}
        </Badge>
      </TableCell>
      <TableCell>{keyIndicator.unit}</TableCell>
      {(story.storiesData.storyType as StoryTypes) === StoryTypes.Impact && (
        <>
          {' '}
          <TableCell>{before}</TableCell>
          <TableCell>{after}</TableCell>
          <TableCell>{change}</TableCell>
        </>
      )}

      {(story.storiesData.storyType as StoryTypes) === StoryTypes.Baseline && (
        <>
          <TableCell>
            {story.storiesData.storyKeyIndicators[0]?.values[0]?.value}
          </TableCell>
        </>
      )}
    </TableRow>
  )
}

const StoryKeyIndicatorsTable = ({
  story,
  categoryData,
}: {
  story: {
    storiesData: StoryWithKeyIndicatorsCategoriesStoryImageAndResources
    innermostParents: {
      storyId: number
      type: CategoryWithParentCategory
    }
  }
  categoryData: CategoryWithChildren[]
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Variable</TableHead>
          <TableHead>Key Indicator</TableHead>
          <TableHead>Unit</TableHead>
          {(story.storiesData.storyType as StoryTypes) ===
            StoryTypes.Impact && (
            <>
              <TableHead>Before</TableHead>
              <TableHead>After</TableHead>
              <TableHead>Change</TableHead>
            </>
          )}

          {(story.storiesData.storyType as StoryTypes) ===
            StoryTypes.Baseline && (
            <>
              <TableHead>
                {story.storiesData.storyKeyIndicators[0]?.values[0]?.year ?? ''}
              </TableHead>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {story.storiesData.storyKeyIndicators.map((keyIndicator) => {
          return (
            <StoryKeyIndicatorsTableRow
              key={keyIndicator.id}
              keyIndicator={keyIndicator}
              categoryData={categoryData}
              story={story}
            />
          )
        })}
      </TableBody>
    </Table>
  )
}

export default StoryKeyIndicatorsTable
